import { useRef, useEffect, useState, useMemo } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Send, MessageSquare, AlertTriangle, CheckCircle, Activity, RotateCcw, ChevronDown, ChevronRight, Shield, ShieldCheck, ShieldX, ShieldQuestion, Wrench, GitBranch, FileCode, Play, XCircle, Clock } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { api } from '../../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'

type Severity = 'P0' | 'P1' | 'P2'
type IssueStatus = 'open' | 'investigating' | 'in_progress' | 'resolved' | 'deployed' | 'monitoring' | 'confirmed_fixed' | 'wontfix' | 'regressed'

const SEV_STYLE: Record<Severity, string> = {
  P0: 'bg-red-50 text-red-700 border-red-200',
  P1: 'bg-amber-50 text-amber-700 border-amber-200',
  P2: 'bg-neutral-50 text-neutral-600 border-neutral-200',
}

const STATUS_STYLE: Record<IssueStatus, string> = {
  open: 'bg-white text-neutral-600 border-neutral-300',
  investigating: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-neutral-200/70 text-neutral-500 border-neutral-300',
  deployed: 'bg-blue-50 text-blue-600 border-blue-200',
  monitoring: 'bg-indigo-50 text-indigo-600 border-indigo-200',
  confirmed_fixed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  wontfix: 'bg-neutral-50 text-neutral-400 border-neutral-200',
  regressed: 'bg-red-50 text-red-700 border-red-300',
}

const STATUS_LABEL: Record<IssueStatus, string> = {
  open: 'Open',
  investigating: 'Investigating',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  deployed: 'Deployed',
  monitoring: 'Monitoring',
  confirmed_fixed: 'Confirmed Fixed',
  wontfix: "Won't Fix",
  regressed: 'Regressed',
}

const ISSUE_STATUSES: IssueStatus[] = ['open', 'investigating', 'in_progress', 'resolved', 'deployed', 'monitoring', 'confirmed_fixed', 'regressed', 'wontfix']

const STATUS_ICON: Record<string, typeof AlertTriangle> = {
  open: AlertTriangle,
  investigating: Activity,
  in_progress: Activity,
  resolved: CheckCircle,
  wontfix: CheckCircle,
  regressed: RotateCcw,
}

function formatTime(ts: number) {
  const diffMs = Date.now() - ts
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHrs = Math.floor(diffMin / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.floor(diffHrs / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatFullTime(ts: number) {
  const d = new Date(ts)
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${date} at ${time}`
}

function formatShortDate(ts: number) {
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const SEV_BORDER: Record<Severity, string> = {
  P0: 'border-l-red-500',
  P1: 'border-l-amber-400',
  P2: 'border-l-neutral-300',
}

// Issue origin badge (mirrors the styling on the issues list). Auto is the
// implicit default and stays unbadged; human signals read warm/strong.
const SOURCE_LABEL: Record<string, string> = { manual: 'Manual', snapshot: 'Snapshot', user_report: 'User report', content_feedback: 'Content' }
const SOURCE_STYLE: Record<string, string> = {
  manual: 'bg-indigo-50 text-indigo-600 border-indigo-100',
  snapshot: 'bg-blue-50 text-blue-600 border-blue-100',
  user_report: 'bg-rose-100 text-rose-700 border-rose-200 font-bold',
  content_feedback: 'bg-amber-50 text-amber-700 border-amber-200',
}

export default function AdminIssueDetailPage() {
  const { issueNumber: raw } = useParams<{ issueNumber: string }>()
  const issueNumber = raw ? Number(raw) : NaN
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin

  const data = useQuery(
    api.admin.getGlobalIssueByNumber,
    isAdmin && Number.isFinite(issueNumber) ? { issueNumber } : 'skip',
  )
  const occurrenceTimeline = useQuery(
    api.admin.getGlobalIssueOccurrenceTimeline,
    isAdmin && Number.isFinite(issueNumber) ? { issueNumber } : 'skip',
  ) as Array<{ at: number; sev: string; email?: string }> | undefined
  const admins = useQuery(api.admin.listAdmins, isAdmin ? {} : 'skip') as Array<{ userId: string; name?: string; email?: string; image?: string }> | undefined
  const setStatus = useMutation(api.admin.setIssueStatus)
  const addComment = useMutation(api.admin.addIssueComment)
  const assignIssue = useMutation(api.admin.assignIssue)
  const triageIssue = useMutation(api.admin.triageIssue)

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500">
        Admin access required.
      </div>
    )
  }
  if (!Number.isFinite(issueNumber)) {
    return <NotFound message={`"${raw}" is not a valid issue number.`} />
  }
  if (data === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    )
  }
  if (!data) {
    return <NotFound message={`No issue ISS-${issueNumber} found.`} />
  }

  const issue = data.issue as any
  const tracking = data.tracking as any
  const status = (tracking?.status ?? 'open') as IssueStatus
  const comments: Array<{ text: string; author: string; createdAt: number }> = tracking?.comments ?? []
  const statusHistory: Array<{ status: string; timestamp: number; author?: string }> = tracking?.statusHistory ?? []
  const verification = tracking?.verification as any
  const triageDecision = tracking?.triageDecision as string | undefined
  const autoFix = tracking?.autoFix as any

  const userName = user?.email?.split('@')[0] || 'admin'

  const occurrences = issue.occurrences ?? []
  // The heavy occurrences array is capped for the 1 MiB doc limit; the timeline
  // holds every occurrence's {at, sev} so the histogram can plot them all.
  const histogramOccurrences = (occurrenceTimeline && occurrenceTimeline.length > 0)
    ? occurrenceTimeline.map(e => ({ occurredAt: e.at, findingSeverity: e.sev, userEmail: e.email }))
    : occurrences

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto max-w-5xl px-6 py-6 space-y-5">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Link to="/admin/issues" className="inline-flex items-center gap-1 hover:text-neutral-800">
            <ArrowLeft className="h-3.5 w-3.5" />
            All issues
          </Link>
        </div>

        {/* Header with severity border */}
        <header className={`rounded-md border border-neutral-200 bg-white p-4 space-y-2.5 border-l-[3px] ${SEV_BORDER[issue.severity as Severity]}`}>
          <div className="flex items-center gap-2 text-[11px] text-neutral-400 font-mono tabular-nums">
            <span className="font-semibold text-neutral-600">ISS-{issue.issueNumber ?? issueNumber}</span>
            <span className="text-neutral-300">·</span>
            <span>{issue.category}</span>
            <span className="text-neutral-300">·</span>
            <span>{issue.occurrenceCount ?? occurrences.length} occurrences</span>
            {typeof issue.signalScore === 'number' && issue.signalScore > (issue.occurrenceCount ?? 0) && (
              <>
                <span className="text-neutral-300">·</span>
                <span className="text-rose-600 font-semibold" title="Weighted signal score (user reports and content feedback count more than auto findings)">signal {issue.signalScore}</span>
              </>
            )}
            {issue.createdBy && (
              <>
                <span className="text-neutral-300">·</span>
                <span>by {issue.createdBy.replace(/@.*/, '')}</span>
              </>
            )}
            {typeof issue.affectedUsers === 'number' && (
              <>
                <span className="text-neutral-300">·</span>
                <span>{issue.affectedUsers} users</span>
              </>
            )}
            {typeof issue.avgSessionScore === 'number' && (
              <>
                <span className="text-neutral-300">·</span>
                <span>avg score {issue.avgSessionScore}/10</span>
              </>
            )}
            {typeof issue.firstSeen === 'number' && (
              <>
                <span className="text-neutral-300">·</span>
                <span>first seen {formatShortDate(issue.firstSeen)}</span>
              </>
            )}
          </div>
          <h1 className="text-lg font-semibold text-neutral-900 leading-snug">{issue.title}</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${SEV_STYLE[issue.severity as Severity]}`}>
              {issue.severity}
            </span>
            {issue.source && issue.source !== 'auto' && SOURCE_STYLE[issue.source] && (
              <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${SOURCE_STYLE[issue.source]}`}>
                {SOURCE_LABEL[issue.source] ?? issue.source}
              </span>
            )}
            <div className="flex rounded-md border border-neutral-200 overflow-hidden">
              {ISSUE_STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus({ issueNumber: issue.issueNumber ?? issueNumber, status: s })}
                  className={`text-[10px] px-2.5 py-1.5 border-r border-neutral-200 last:border-r-0 transition-colors ${
                    status === s
                      ? STATUS_STYLE[s] + ' font-semibold'
                      : 'bg-white text-neutral-400 hover:bg-neutral-50 hover:text-neutral-600'
                  }`}
                >
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
            {admins && admins.length > 0 && (
              <div className="flex items-center gap-1.5">
                {(() => {
                  const aa = tracking?.assignee ? admins.find(a => a.userId === tracking.assignee) : null
                  if (aa?.image) return <img src={aa.image} alt="" className="w-5 h-5 rounded-full object-cover" />
                  return null
                })()}
                <select
                  value={tracking?.assignee || ''}
                  onChange={(e) =>
                    assignIssue({ issueNumber: issue.issueNumber ?? issueNumber, assignee: e.target.value || undefined })
                  }
                  className="text-[11px] px-2 py-0.5 rounded border border-neutral-200 bg-white cursor-pointer"
                >
                  <option value="">Unassigned</option>
                  {admins.map((a) => (
                    <option key={a.userId} value={a.userId}>{a.name || a.email || a.userId}</option>
                  ))}
                </select>
              </div>
            )}
            {issue.tags?.map((t: string) => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-600 border border-sky-100">#{t}</span>
            ))}
            {issue.skills?.slice(0, 6).map((s: { name: string; count: number }) => (
              <span key={s.name} className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 border border-violet-100">
                {s.name} <span className="text-violet-400">{s.count}</span>
              </span>
            ))}
          </div>
        </header>

        {/* Verification + Triage + Auto-fix */}
        <VerificationPanel
          verification={verification}
          triageDecision={triageDecision}
          autoFix={autoFix}
          onTriage={(decision) => triageIssue({ issueNumber: issue.issueNumber ?? issueNumber, decision })}
        />

        {/* Occurrence volume over time */}
        {histogramOccurrences.length > 0 && (
          <OccurrenceHistogram occurrences={histogramOccurrences} statusHistory={statusHistory} comments={comments} totalCount={issue.occurrenceCount} />
        )}

        {/* Activity — first, most recent on top */}
        <IssueTimeline
          comments={comments}
          statusHistory={statusHistory}
          onAddComment={async (text: string) => {
            await addComment({ issueNumber: issue.issueNumber ?? issueNumber, text, author: userName })
          }}
        />

        {/* Analysis — collapsed by default */}
        <CollapsibleSection label="Analysis" defaultOpen={false}>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              {issue.userImpact ? (
                <ContextBlock label="User impact" body={issue.userImpact} accent="red" />
              ) : (
                <EnrichingPlaceholder label="User impact" />
              )}
              <ContextBlock label="Root cause hypothesis" body={issue.rootCauseHypothesis} />
              {issue.userReactionPattern ? (
                <ContextBlock label="User reaction pattern" body={issue.userReactionPattern} italic />
              ) : (
                <EnrichingPlaceholder label="User reaction pattern" />
              )}
            </div>
            <div className="space-y-4">
              {issue.reproSteps ? (
                <ContextBlock label="Repro steps" body={issue.reproSteps} mono />
              ) : (
                <EnrichingPlaceholder label="Repro steps" />
              )}
              {issue.suggestedInvestigation ? (
                <ContextBlock label="Suggested investigation" body={issue.suggestedInvestigation} accent="blue" />
              ) : (
                <EnrichingPlaceholder label="Suggested investigation" />
              )}
            </div>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  )
}

// ─── Verification / Triage / Auto-fix ──────────────────────────────────────

const VERIFICATION_STYLES: Record<string, { bg: string; text: string; icon: typeof Shield; label: string }> = {
  pending: { bg: 'bg-neutral-50 border-neutral-200', text: 'text-neutral-500', icon: Clock, label: 'Pending verification' },
  in_progress: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-600', icon: Shield, label: 'Verifying...' },
  verified: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', icon: ShieldCheck, label: 'Verified bug' },
  false_positive: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', icon: ShieldX, label: 'False positive' },
  inconclusive: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', icon: ShieldQuestion, label: 'Inconclusive' },
}

const AUTOFIX_STAGES = [
  { key: 'pending', label: 'Queued' },
  { key: 'fixing', label: 'Fixing' },
  { key: 'fix_complete', label: 'Fixed' },
  { key: 'validating', label: 'Validating' },
  { key: 'validated', label: 'Validated' },
  { key: 'reviewing', label: 'Reviewing' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'ready', label: 'Ready' },
  { key: 'deployed', label: 'Deployed' },
  { key: 'monitoring', label: 'Monitoring' },
]

function VerificationPanel({
  verification,
  triageDecision,
  autoFix,
  onTriage,
}: {
  verification: any
  triageDecision: string | undefined
  autoFix: any
  onTriage: (decision: 'manual' | 'auto' | 'wontfix') => void
}) {
  const [triaging, setTriaging] = useState(false)
  const status = verification?.status ?? 'pending'
  const style = VERIFICATION_STYLES[status] ?? VERIFICATION_STYLES.pending
  const Icon = style.icon

  const handleTriage = async (decision: 'manual' | 'auto' | 'wontfix') => {
    setTriaging(true)
    try { onTriage(decision) } finally { setTriaging(false) }
  }

  return (
    <div className="space-y-3">
      {/* Verification status */}
      <div className={`rounded-md border ${style.bg} p-4`}>
        <div className="flex items-center gap-2 mb-2">
          <Icon className={`h-4 w-4 ${style.text}`} />
          <span className={`text-xs font-semibold uppercase tracking-wide ${style.text}`}>{style.label}</span>
          {status === 'in_progress' && <Loader2 className="h-3 w-3 animate-spin text-blue-400" />}
          {verification?.completedAt && (
            <span className="text-[10px] text-neutral-400 ml-auto">{formatTime(verification.completedAt)}</span>
          )}
        </div>

        {verification?.analysis && (
          <div className="text-xs text-neutral-700 leading-relaxed mb-3 whitespace-pre-wrap">
            <MarkdownBody text={verification.analysis} />
          </div>
        )}

        {verification?.confirmedRootCause && status === 'verified' && (
          <div className="rounded bg-red-100/50 border border-red-200 px-3 py-2 mb-3">
            <div className="text-[9px] uppercase tracking-wider text-red-600 font-semibold mb-1">Confirmed root cause</div>
            <div className="text-xs text-red-800">{verification.confirmedRootCause}</div>
          </div>
        )}

        {verification?.codeEvidence && (
          <div className="rounded bg-neutral-100 px-3 py-2 mb-3">
            <div className="text-[9px] uppercase tracking-wider text-neutral-500 font-semibold mb-1">Code evidence</div>
            <pre className="text-[11px] text-neutral-700 font-mono whitespace-pre-wrap overflow-x-auto">{verification.codeEvidence}</pre>
          </div>
        )}

        {verification?.relevantFiles?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {verification.relevantFiles.map((f: string) => (
              <span key={f} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-neutral-200 font-mono text-neutral-600">
                <FileCode className="h-3 w-3 inline mr-0.5 -mt-0.5" />{f}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Triage buttons — only when verified */}
      {status === 'verified' && !triageDecision && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs font-semibold text-amber-800 mb-3">Triage required — how should this be fixed?</div>
          <div className="flex gap-2">
            <button
              onClick={() => handleTriage('auto')}
              disabled={triaging}
              className="text-xs px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5 font-medium"
            >
              <Play className="h-3 w-3" />
              Auto-fix (remote agent)
            </button>
            <button
              onClick={() => handleTriage('manual')}
              disabled={triaging}
              className="text-xs px-3 py-2 rounded-md bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 inline-flex items-center gap-1.5 font-medium"
            >
              <Wrench className="h-3 w-3" />
              Fix manually
            </button>
            <button
              onClick={() => handleTriage('wontfix')}
              disabled={triaging}
              className="text-xs px-3 py-2 rounded-md bg-white border border-neutral-200 text-neutral-400 hover:text-neutral-600 hover:border-neutral-300 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <XCircle className="h-3 w-3" />
              Won't fix
            </button>
          </div>
        </div>
      )}

      {/* Triage decision badge */}
      {triageDecision && (
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-neutral-400">Triage:</span>
          <span className={`px-2 py-0.5 rounded-full font-medium ${
            triageDecision === 'auto' ? 'bg-blue-100 text-blue-700' :
            triageDecision === 'manual' ? 'bg-neutral-100 text-neutral-600' :
            'bg-neutral-100 text-neutral-400'
          }`}>
            {triageDecision === 'auto' ? 'Auto-fix' : triageDecision === 'manual' ? 'Manual fix' : "Won't fix"}
          </span>
        </div>
      )}

      {/* Auto-fix pipeline tracker */}
      {autoFix && <AutoFixTracker autoFix={autoFix} />}
    </div>
  )
}

function AutoFixTracker({ autoFix }: { autoFix: any }) {
  const currentStatus = autoFix.status ?? 'pending'
  const isFailed = currentStatus === 'failed'
  const isApplied = currentStatus === 'applied'
  const currentIdx = AUTOFIX_STAGES.findIndex(s => s.key === currentStatus)

  return (
    <div className={`rounded-md border p-4 ${isFailed ? 'border-red-200 bg-red-50' : isApplied ? 'border-emerald-200 bg-emerald-50' : 'border-blue-200 bg-blue-50'}`}>
      <div className="flex items-center gap-2 mb-3">
        <GitBranch className={`h-4 w-4 ${isFailed ? 'text-red-500' : isApplied ? 'text-emerald-600' : 'text-blue-600'}`} />
        <span className={`text-xs font-semibold uppercase tracking-wide ${isFailed ? 'text-red-700' : isApplied ? 'text-emerald-700' : 'text-blue-700'}`}>
          {isFailed ? 'Auto-fix failed' : isApplied ? 'Fix applied' : 'Auto-fix pipeline'}
        </span>
        {autoFix.branch && (
          <span className="text-[10px] font-mono text-neutral-500 bg-white px-1.5 py-0.5 rounded border border-neutral-200">{autoFix.branch}</span>
        )}
      </div>

      {/* Stage progress bar */}
      {!isApplied && (
        <div className="flex items-center gap-0.5 mb-3">
          {AUTOFIX_STAGES.map((stage, i) => {
            const done = i < currentIdx || currentStatus === 'ready'
            const active = stage.key === currentStatus || (currentStatus === 'ready' && i === AUTOFIX_STAGES.length - 1)
            const failed = isFailed && i === currentIdx
            return (
              <div key={stage.key} className="flex-1 flex flex-col items-center gap-1">
                <div className={`h-1.5 w-full rounded-full ${
                  failed ? 'bg-red-400' :
                  done ? 'bg-blue-500' :
                  active ? 'bg-blue-400 animate-pulse' :
                  'bg-neutral-200'
                }`} />
                <span className={`text-[9px] ${
                  failed ? 'text-red-600 font-semibold' :
                  done || active ? 'text-blue-600 font-medium' :
                  'text-neutral-400'
                }`}>{stage.label}</span>
              </div>
            )
          })}
        </div>
      )}

      {/* Error message */}
      {autoFix.error && (
        <div className="rounded bg-red-100 border border-red-200 px-3 py-2 mb-3 text-xs text-red-800">
          {autoFix.error}
        </div>
      )}

      {/* Fix analysis */}
      {autoFix.fixAnalysis && (
        <CollapsibleSection label="Fix analysis" defaultOpen={currentStatus === 'ready'}>
          <div className="text-xs text-neutral-700 whitespace-pre-wrap">{autoFix.fixAnalysis}</div>
        </CollapsibleSection>
      )}

      {/* Diff */}
      {autoFix.fixDiff && (
        <CollapsibleSection label="Diff" defaultOpen={false}>
          <pre className="text-[11px] font-mono text-neutral-700 whitespace-pre-wrap overflow-x-auto max-h-[400px] overflow-y-auto">{autoFix.fixDiff}</pre>
        </CollapsibleSection>
      )}

      {/* Validation result */}
      {autoFix.validationResult && (
        <CollapsibleSection label="Validation" defaultOpen={!autoFix.validationResult.passed}>
          <div className="space-y-2">
            <div className={`text-xs font-semibold ${autoFix.validationResult.passed ? 'text-emerald-600' : 'text-red-600'}`}>
              {autoFix.validationResult.passed ? '✓ Passed' : '✗ Failed'}
            </div>
            <div className="text-xs text-neutral-700 whitespace-pre-wrap">{autoFix.validationResult.analysis}</div>
            {autoFix.validationResult.concerns?.length > 0 && (
              <ul className="text-xs text-amber-700 list-disc pl-4 space-y-0.5">
                {autoFix.validationResult.concerns.map((c: string, i: number) => <li key={i}>{c}</li>)}
              </ul>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Review result */}
      {autoFix.reviewResult && (
        <CollapsibleSection label="Code review" defaultOpen={autoFix.reviewResult.verdict !== 'approve'}>
          <div className="space-y-2">
            <div className={`text-xs font-semibold ${
              autoFix.reviewResult.verdict === 'approve' ? 'text-emerald-600' :
              autoFix.reviewResult.verdict === 'reject' ? 'text-red-600' :
              'text-amber-600'
            }`}>
              {autoFix.reviewResult.verdict === 'approve' ? '✓ Approved' : autoFix.reviewResult.verdict === 'reject' ? '✗ Rejected' : '⚠ Needs changes'}
            </div>
            <div className="text-xs text-neutral-700 whitespace-pre-wrap">{autoFix.reviewResult.analysis}</div>
            {autoFix.reviewResult.suggestions?.length > 0 && (
              <ul className="text-xs text-blue-700 list-disc pl-4 space-y-0.5">
                {autoFix.reviewResult.suggestions.map((s: string, i: number) => <li key={i}>{s}</li>)}
              </ul>
            )}
          </div>
        </CollapsibleSection>
      )}
    </div>
  )
}

type TimelineEvent = {
  ts: number
  kind: 'comment' | 'status' | 'occurrence'
  title: string
  body?: string
  author?: string
  meta?: string
  variant: 'good' | 'bad' | 'neutral' | 'warn'
}

function IssueTimeline({
  comments,
  statusHistory,
  onAddComment,
}: {
  comments: Array<{ text: string; author: string; createdAt: number }>
  statusHistory: Array<{ status: string; timestamp: number; author?: string }>
  onAddComment: (text: string) => Promise<void>
}) {
  const events: TimelineEvent[] = []

  for (const c of comments) {
    events.push({ ts: c.createdAt, kind: 'comment', title: c.author, body: c.text, author: c.author, variant: 'neutral' })
  }

  for (const h of statusHistory) {
    const variant: TimelineEvent['variant'] = h.status === 'resolved' ? 'good' : h.status === 'regressed' ? 'bad' : h.status === 'wontfix' ? 'neutral' : 'warn'
    events.push({ ts: h.timestamp, kind: 'status', title: `Status changed to ${STATUS_LABEL[h.status as IssueStatus] ?? h.status}`, author: h.author, variant })
  }

  events.sort((a, b) => b.ts - a.ts)

  return (
    <section className="space-y-3">
      <h2 className="text-xs uppercase tracking-wide text-neutral-400">Activity ({events.length})</h2>
      <CommentInput onSubmit={onAddComment} />
      {events.length === 0 ? (
        <div className="text-xs text-neutral-500 italic">No activity yet.</div>
      ) : (
        <div className="relative pl-6">
          <div className="absolute left-[9px] top-2 bottom-2 w-px bg-neutral-200" />
          <ul className="space-y-3">
            {events.map((e, i) => <TimelineRow key={i} event={e} />)}
          </ul>
        </div>
      )}
    </section>
  )
}

function TimelineRow({ event }: { event: TimelineEvent }) {
  const Icon = event.kind === 'comment' ? MessageSquare : event.kind === 'status' ? (STATUS_ICON[event.title.split(' ').pop()?.toLowerCase() ?? ''] ?? Activity) : AlertTriangle

  const dotColor = event.variant === 'good' ? 'bg-emerald-500 ring-emerald-100' : event.variant === 'bad' ? 'bg-red-500 ring-red-100' : event.variant === 'warn' ? 'bg-amber-500 ring-amber-100' : 'bg-neutral-400 ring-neutral-100'

  return (
    <li className="relative">
      <div className={`absolute -left-[19px] top-1 w-[10px] h-[10px] rounded-full ring-4 ${dotColor}`} />
      <div className="rounded-md bg-white border border-neutral-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-50 border-b border-neutral-100">
          <Icon className="h-3.5 w-3.5 text-neutral-500" />
          <span className="text-[11px] font-semibold text-neutral-700">{event.title}</span>
          {event.meta && <span className="text-[10px] text-neutral-500">{event.meta}</span>}
          <span className="ml-auto text-[10px] text-neutral-400" title={formatFullTime(event.ts)}>{formatTime(event.ts)}</span>
        </div>
        {event.body && (
          <div className="px-3 py-2 text-xs text-neutral-700 issue-comment-markdown">
            {event.kind === 'comment' ? <MarkdownBody text={event.body} /> : <div className="whitespace-pre-wrap">{event.body}</div>}
          </div>
        )}
      </div>
    </li>
  )
}

function MarkdownBody({ text }: { text: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
        a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">{children}</a>,
        code: ({ children, className }) => {
          const isBlock = className?.includes('language-')
          return isBlock ? <code className="block bg-neutral-100 rounded px-2 py-1.5 my-1.5 text-[11px] font-mono whitespace-pre-wrap overflow-x-auto">{children}</code> : <code className="bg-neutral-100 rounded px-1 py-0.5 text-[11px] font-mono">{children}</code>
        },
        pre: ({ children }) => <>{children}</>,
        ul: ({ children }) => <ul className="list-disc pl-4 mb-1.5 space-y-0.5">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-4 mb-1.5 space-y-0.5">{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        strong: ({ children }) => <strong className="font-semibold text-neutral-800">{children}</strong>,
        h1: ({ children }) => <div className="font-semibold text-neutral-800 text-sm mt-2 mb-1">{children}</div>,
        h2: ({ children }) => <div className="font-semibold text-neutral-800 text-xs mt-2 mb-1">{children}</div>,
        h3: ({ children }) => <div className="font-semibold text-neutral-700 text-xs mt-1.5 mb-0.5">{children}</div>,
        blockquote: ({ children }) => <blockquote className="border-l-2 border-neutral-300 pl-2 ml-1 text-neutral-500 italic">{children}</blockquote>,
        hr: () => <hr className="border-neutral-200 my-2" />,
        table: ({ children }) => <table className="w-full text-[11px] border-collapse my-2">{children}</table>,
        thead: ({ children }) => <thead className="bg-neutral-50">{children}</thead>,
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => <tr className="border-b border-neutral-100">{children}</tr>,
        th: ({ children }) => <th className="text-left px-2 py-1 font-semibold text-neutral-600 border-b border-neutral-200">{children}</th>,
        td: ({ children }) => <td className="px-2 py-1 text-neutral-700">{children}</td>,
      }}
    >
      {text}
    </ReactMarkdown>
  )
}

// ─── Occurrence timeline (dot-stack histogram + event markers) ────────────────
// Each occurrence is one dot; dots stack vertically within fine time bins so a
// column's height equals the true count in that bin — accurate volume over time.

type EventMarker = { ts: number; type: 'status' | 'comment'; label: string; detail: string; color: string }
type StackedDot = { ts: number; sev: string; email: string; desc: string; cx: number; stackIdx: number }

const SEV_COLOR: Record<string, string> = { severe: '#dc2626', moderate: '#d97706', minor: '#a3a3a3' }
const SEV_ORDER: Record<string, number> = { severe: 0, moderate: 1, minor: 2 }

function volumeTicks(max: number): number[] {
  if (max <= 1) return [0, 1]
  const step = max <= 4 ? 1 : max <= 10 ? 2 : max <= 25 ? 5 : max <= 60 ? 10 : max <= 150 ? 25 : Math.ceil(max / 4 / 50) * 50
  const out: number[] = []
  for (let v = 0; v <= max; v += step) out.push(v)
  return out
}

function OccurrenceHistogram({
  occurrences,
  statusHistory,
  comments,
  totalCount,
}: {
  occurrences: Array<{ timestampMs?: number; occurredAt?: number; findingSeverity?: string; userEmail?: string; description?: string }>
  statusHistory: Array<{ status: string; timestamp: number; author?: string }>
  comments?: Array<{ text: string; author: string; createdAt: number }>
  totalCount?: number
}) {
  const [hoverX, setHoverX] = useState<number | null>(null)
  const [hoverEvent, setHoverEvent] = useState<number | null>(null)
  const [mouseX, setMouseX] = useState(0)
  const [containerW, setContainerW] = useState(800)

  const w = 1200
  const h = 150
  const axisY = h - 16
  const plotTop = 8
  const plotH = axisY - plotTop
  const colPitch = 6
  const numCols = Math.max(1, Math.floor(w / colPitch))

  const data = useMemo(() => {
    const dayMs = 86400000

    const raw = occurrences
      .map(o => ({ ts: o.occurredAt ?? o.timestampMs ?? 0, sev: o.findingSeverity || 'minor', email: o.userEmail || '', desc: o.description || '' }))
      .filter(e => e.ts > 0)
    const sts = statusHistory.filter(s => s.timestamp > 0)
    const cmts = (comments || []).filter(c => c.createdAt > 0)

    const allTs = [...raw.map(e => e.ts), ...sts.map(e => e.timestamp), ...cmts.map(e => e.createdAt)]
    if (allTs.length === 0) return null

    const minTs = Math.min(...allTs)
    const maxTs = Math.max(...allTs)
    const span = Math.max(maxTs - minTs, dayMs)
    const pad = span * 0.02
    const startTs = minTs - pad
    const range = span + pad * 2

    const sorted = [...raw].sort((a, b) => (SEV_ORDER[a.sev] ?? 2) - (SEV_ORDER[b.sev] ?? 2))

    const colItems: typeof sorted[] = Array.from({ length: numCols }, () => [])
    for (const o of sorted) {
      const col = Math.max(0, Math.min(numCols - 1, Math.floor(((o.ts - startTs) / range) * numCols)))
      colItems[col].push(o)
    }
    let maxStack = 1
    const dots: StackedDot[] = []
    colItems.forEach((items, col) => {
      if (items.length > maxStack) maxStack = items.length
      const cx = (col + 0.5) * (w / numCols)
      items.forEach((o, j) => dots.push({ ...o, cx, stackIdx: j }))
    })

    const rangeDays = range / dayMs
    const tickCount = Math.min(8, Math.max(2, Math.ceil(rangeDays > 14 ? rangeDays / 7 : rangeDays)))
    const ticks = Array.from({ length: tickCount + 1 }, (_, i) => {
      const ts = startTs + (i / tickCount) * range
      return { ts, label: new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }
    })

    const events: EventMarker[] = []
    for (const s of sts) {
      const color = s.status === 'resolved' ? '#059669' : s.status === 'regressed' ? '#dc2626' : s.status === 'investigating' || s.status === 'in_progress' ? '#d97706' : '#737373'
      events.push({ ts: s.timestamp, type: 'status', label: STATUS_LABEL[s.status as IssueStatus] || s.status, detail: s.author ? `by ${s.author}` : '', color })
    }
    for (const c of cmts) {
      events.push({ ts: c.createdAt, type: 'comment', label: c.author, detail: c.text.slice(0, 60), color: '#2563eb' })
    }
    events.sort((a, b) => a.ts - b.ts)

    const sc = { severe: 0, moderate: 0, minor: 0 }
    raw.forEach(e => { if (e.sev in sc) sc[e.sev as keyof typeof sc]++ })

    return { dots, events, startTs, range, ticks, sc, plotted: raw.length, maxStack }
  }, [occurrences, statusHistory, comments, numCols])

  if (!data) return null
  const { dots, events, startTs, range, ticks, sc, plotted, maxStack } = data
  const displayTotal = totalCount ?? plotted

  const dotPitch = Math.min(5, plotH / maxStack)
  const dotR = Math.max(0.9, Math.min(2.4, dotPitch / 2 - 0.35))
  const toX = (ts: number) => Math.max(0, Math.min(w, ((ts - startTs) / range) * w))
  const yTicks = volumeTicks(maxStack)

  const hoverTs = hoverX != null ? startTs + (hoverX / w) * range : null
  const bandHalf = (7 / w) * range
  const nearbyDots = hoverTs != null ? dots.filter(d => Math.abs(d.ts - hoverTs) < bandHalf) : []
  const tooltipW = 280
  const tipLeft = Math.max(0, Math.min(mouseX - tooltipW / 2, containerW - tooltipW))

  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50/40 p-4 relative">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3 text-[10px] text-neutral-400">
          <span className="uppercase tracking-widest font-semibold text-neutral-500 text-[9px]">Volume over time</span>
          <span className="flex items-center gap-3">
            {sc.severe > 0 && <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: SEV_COLOR.severe }} />{sc.severe} severe</span>}
            {sc.moderate > 0 && <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: SEV_COLOR.moderate }} />{sc.moderate} moderate</span>}
            {sc.minor > 0 && <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: SEV_COLOR.minor }} />{sc.minor} minor</span>}
          </span>
          {events.length > 0 && (
            <span className="flex items-center gap-3 border-l border-neutral-200 pl-3">
              {events.some(e => e.type === 'status') && <span className="flex items-center gap-1"><span className="inline-block w-0 h-0 border-l-[3px] border-r-[3px] border-b-[4px] border-l-transparent border-r-transparent border-b-emerald-500" />status</span>}
              {events.some(e => e.type === 'comment') && <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />comment</span>}
            </span>
          )}
        </div>
        <span className="text-[11px] font-mono tabular-nums text-neutral-500">
          {plotted === displayTotal ? `${plotted} occurrences` : `${plotted} plotted of ${displayTotal}`}
        </span>
      </div>

      <svg
        width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block"
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          const px = e.clientX - rect.left
          setMouseX(px)
          setContainerW(rect.width)
          setHoverX((px / rect.width) * w)
          setHoverEvent(null)
        }}
        onMouseLeave={() => { setHoverX(null); setHoverEvent(null) }}
      >
        {/* Volume gridlines (y-axis = occurrence count) */}
        {yTicks.map((v) => {
          const y = axisY - v * dotPitch
          return (
            <g key={`y${v}`}>
              <line x1={0} y1={y} x2={w} y2={y} stroke="#eee" strokeWidth={0.5} />
              <text x={2} y={y - 1.5} fontSize={6.5} fill="#bbb" className="tabular-nums">{v}</text>
            </g>
          )
        })}
        <line x1={0} y1={axisY} x2={w} y2={axisY} stroke="#d4d4d4" strokeWidth={0.6} />

        {hoverX != null && (
          <rect x={hoverX - 7} y={plotTop} width={14} height={plotH} fill="#000" opacity={0.04} rx={2} />
        )}

        {dots.map((d, i) => {
          const cy = axisY - (d.stackIdx + 0.5) * dotPitch
          return <circle key={i} cx={d.cx} cy={cy} r={dotR} fill={SEV_COLOR[d.sev] || '#a3a3a3'} opacity={0.78} />
        })}

        {events.map((ev, i) => {
          const x = toX(ev.ts)
          const isHovered = hoverEvent === i
          if (ev.type === 'status') {
            return (
              <g key={`ev${i}`} onMouseEnter={() => { setHoverEvent(i); setHoverX(null) }} style={{ cursor: 'pointer' }}>
                <line x1={x} y1={plotTop} x2={x} y2={axisY} stroke={ev.color} strokeWidth={isHovered ? 1.5 : 0.9} strokeDasharray="3,2" opacity={isHovered ? 0.9 : 0.45} />
                <polygon points={`${x},${axisY - 6} ${x - 3.5},${axisY + 1} ${x + 3.5},${axisY + 1}`} fill={ev.color} opacity={isHovered ? 1 : 0.85} />
              </g>
            )
          }
          return (
            <g key={`ev${i}`} onMouseEnter={() => { setHoverEvent(i); setHoverX(null) }} style={{ cursor: 'pointer' }}>
              <circle cx={x} cy={axisY + 4} r={isHovered ? 3.5 : 2.6} fill={ev.color} opacity={isHovered ? 1 : 0.6} />
            </g>
          )
        })}

        {ticks.map((t, i) => {
          const x = toX(t.ts)
          return (
            <text key={`t${i}`} x={x} y={h - 1} textAnchor={i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle'} fontSize={7} fill="#a3a3a3">{t.label}</text>
          )
        })}
      </svg>

      {nearbyDots.length > 0 && (
        <div className="absolute z-20 bg-neutral-900 text-white rounded-md shadow-lg px-3 py-2 text-[10px] pointer-events-none" style={{ top: 28, left: tipLeft, width: tooltipW }}>
          <div className="flex items-baseline justify-between mb-1">
            <span className="font-semibold text-[12px]">{nearbyDots.length} occurrence{nearbyDots.length !== 1 ? 's' : ''}</span>
            <span className="text-neutral-400 font-mono text-[9px]">{new Date(hoverTs!).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' })}</span>
          </div>
          <div className="flex gap-2 text-[10px] mb-1.5">
            {(() => { const s = nearbyDots.filter(d => d.sev === 'severe').length; return s > 0 ? <span className="text-red-300">{s} severe</span> : null })()}
            {(() => { const m = nearbyDots.filter(d => d.sev === 'moderate').length; return m > 0 ? <span className="text-amber-300">{m} moderate</span> : null })()}
            {(() => { const n = nearbyDots.filter(d => d.sev === 'minor').length; return n > 0 ? <span className="text-neutral-400">{n} minor</span> : null })()}
          </div>
          <div className="space-y-0.5 max-h-[80px] overflow-hidden border-t border-neutral-700 pt-1.5">
            {nearbyDots.slice(0, 3).map((d, j) => (
              <div key={j} className="text-neutral-400 truncate">{d.email}: {d.desc.slice(0, 60)}</div>
            ))}
            {nearbyDots.length > 3 && <div className="text-neutral-500">+{nearbyDots.length - 3} more</div>}
          </div>
        </div>
      )}
      {hoverEvent != null && events[hoverEvent] && (
        <div className="absolute z-20 bg-neutral-900 text-white rounded-md shadow-lg px-3 py-2 text-[10px] max-w-[240px] pointer-events-none" style={{ top: 28, left: Math.max(0, Math.min(mouseX - 120, containerW - 240)) }}>
          <div className="font-mono text-neutral-300 mb-0.5">
            {new Date(events[hoverEvent].ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </div>
          <div className="font-semibold" style={{ color: events[hoverEvent].color }}>
            {events[hoverEvent].type === 'status' ? `Status → ${events[hoverEvent].label}` : `Comment by ${events[hoverEvent].label}`}
          </div>
          {events[hoverEvent].detail && <div className="text-neutral-400 mt-0.5">{events[hoverEvent].detail}</div>}
        </div>
      )}
    </div>
  )
}

// ─── Collapsible section ────────────────────────────────────────────────────

function CollapsibleSection({ label, children, defaultOpen = true }: { label: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <section className="space-y-3">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 group w-full py-1">
        {open ? <ChevronDown className="h-3.5 w-3.5 text-neutral-400" /> : <ChevronRight className="h-3.5 w-3.5 text-neutral-300 group-hover:text-neutral-500 transition-colors" />}
        <h2 className="text-[10px] uppercase tracking-widest text-neutral-400 group-hover:text-neutral-600 font-semibold transition-colors">{label}</h2>
        <span className="flex-1 border-t border-neutral-100" />
      </button>
      {open && <div className="rounded-lg border border-neutral-200 bg-white p-4">{children}</div>}
    </section>
  )
}

function NotFound({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-3">
        <div className="text-sm text-neutral-500">{message}</div>
        <Link to="/admin/issues" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to all issues
        </Link>
      </div>
    </div>
  )
}

function ContextBlock({ label, body, mono, italic, accent }: { label: string; body: string; mono?: boolean; italic?: boolean; accent?: 'red' | 'blue' }) {
  const border = accent === 'red' ? 'border-l-red-400' : accent === 'blue' ? 'border-l-blue-400' : 'border-l-neutral-200'
  const labelClass = accent === 'red' ? 'text-red-700' : accent === 'blue' ? 'text-blue-700' : 'text-neutral-500'
  return (
    <div className={`rounded-md border border-neutral-100 border-l-2 ${border} bg-white px-3.5 py-3`}>
      <div className={`text-[9px] uppercase tracking-wider mb-1.5 font-semibold ${labelClass}`}>{label}</div>
      <p className={`text-[11.5px] text-neutral-700 leading-relaxed whitespace-pre-wrap ${mono ? 'font-mono text-[11px]' : ''} ${italic ? 'italic' : ''}`}>{body}</p>
    </div>
  )
}

function EnrichingPlaceholder({ label }: { label: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-neutral-400 mb-1">{label}</div>
      <div className="flex items-center gap-2 text-xs text-neutral-400 italic">
        <Loader2 className="h-3 w-3 animate-spin" />
        enriching...
      </div>
    </div>
  )
}

function CommentInput({ onSubmit }: { onSubmit: (text: string) => Promise<void> }) {
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const el = textareaRef.current
    if (el) { el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 200)}px` }
  }, [text])

  const handleSubmit = async () => {
    if (!text.trim() || submitting) return
    setSubmitting(true)
    try { await onSubmit(text.trim()); setText('') } finally { setSubmitting(false) }
  }

  return (
    <div className="rounded-md border border-neutral-200 bg-white p-3 space-y-2">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSubmit() } }}
        placeholder="Add a comment (markdown supported, Cmd+Enter to send)"
        rows={2}
        className="w-full text-xs text-neutral-800 placeholder-neutral-400 focus:outline-none resize-none"
      />
      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={!text.trim() || submitting}
          className="text-xs px-2.5 py-1 rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:bg-neutral-300 disabled:cursor-not-allowed inline-flex items-center gap-1"
        >
          <Send className="h-3 w-3" />
          {submitting ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  )
}
